/**
 * @module components/layout/AppShell
 * @description Main application shell providing the responsive grid layout,
 * sticky sidebar for desktop/tablet, edge-to-edge iOS bottom tab bar for mobile,
 * white-label Client Organization `theme_color` synchronization, and logout confirmation.
 */

import * as React from "react";
import { SidebarNav } from "./SidebarNav";
import { MobileTabBar } from "./MobileTabBar";
import { LogOut, AlertTriangle, Sun, Moon } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { APP_NAME } from "@/config/env";
import { applyClientThemeColor } from "@/features/clients/types";
import type { ClientBrandingDto } from "@/types/auth";

interface AppShellProps {
  children: React.ReactNode;
  currentUser?: {
    name: string;
    email_id: string;
    role: string;
    avatar_url: string;
    client?: ClientBrandingDto | null;
  } | null;
  onLogout?: () => void;
}

export function AppShell({ children, currentUser, onLogout }: AppShellProps) {
  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);
  const [isDark, setIsDark] = React.useState(() => {
    return document.documentElement.classList.contains("dark");
  });

  React.useEffect(() => {
    const themeColor = currentUser?.client?.theme_color;
    if (themeColor && /^#[0-9a-fA-F]{6}$/.test(themeColor)) {
      applyClientThemeColor(themeColor);
    }
  }, [currentUser?.client?.theme_color]);

  const toggleTheme = React.useCallback(() => {
    const nextIsDark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", nextIsDark);
    localStorage.setItem("theme", nextIsDark ? "dark" : "light");
    setIsDark(nextIsDark);
  }, []);

  const handleRequestLogout = React.useCallback(() => {
    setShowLogoutConfirm(true);
  }, []);

  const handleConfirmLogout = React.useCallback(() => {
    setShowLogoutConfirm(false);
    onLogout?.();
  }, [onLogout]);

  const isClientRole = currentUser?.role === "client";

  return (
    <div className="flex min-h-screen w-full bg-background text-foreground antialiased selection:bg-zinc-200 dark:selection:bg-zinc-800 selection:text-foreground">
      {/* Sidebar: Desktop expanded (256px) & Tablet icon rail (64px) */}
      <SidebarNav currentUser={currentUser} onLogout={handleRequestLogout} />

      {/* Main Content Area */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Mobile Top Header (<640px) */}
        <header className="flex sm:hidden h-14 items-center justify-between border-b border-border/70 dark:border-white/[0.08] bg-card/90 dark:bg-black/95 px-4 backdrop-blur-xl sticky top-0 z-30">
          <div className="flex items-center gap-2 min-w-0">
            <div
              style={
                isClientRole
                  ? { borderColor: "var(--color-brand-primary)" }
                  : undefined
              }
              className="h-6 w-6 rounded-full border-[2px] border-foreground flex items-center justify-center shrink-0"
            >
              <div
                style={
                  isClientRole
                    ? { backgroundColor: "var(--color-brand-primary)" }
                    : undefined
                }
                className="h-1.5 w-1.5 rounded-full bg-foreground"
              />
            </div>
            <span className="font-bold text-sm tracking-tight text-foreground font-sans truncate">
              {currentUser?.client?.org_name || `${APP_NAME.toLowerCase()}.`}
            </span>
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-muted font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
              {isClientRole ? "client" : "s-admin"}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={toggleTheme}
              className="h-8 w-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 dark:hover:bg-zinc-900 transition-colors"
              title={isDark ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDark ? (
                <Sun className="h-4 w-4 text-amber-500" />
              ) : (
                <Moon className="h-4 w-4 text-foreground" />
              )}
            </button>

            {currentUser && (
              <>
                <img
                  src={currentUser.avatar_url}
                  alt={currentUser.name}
                  className="h-7 w-7 rounded-full bg-muted object-cover ring-1 ring-border/80 dark:ring-white/10"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><rect width='32' height='32' fill='%2318181b'/><text x='16' y='21' font-size='13' font-family='sans-serif' font-weight='600' text-anchor='middle' fill='white'>AD</text></svg>";
                  }}
                />
                <button
                  type="button"
                  onClick={handleRequestLogout}
                  className="h-8 w-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 transition-colors"
                  title="Sign out of account"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="sr-only">Sign out</span>
                </button>
              </>
            )}
          </div>
        </header>

        {/* Scrollable Page Body */}
        <main className="flex-1 p-3.5 pb-[calc(56px+env(safe-area-inset-bottom,0px)+1.5rem)] sm:p-6 sm:pb-8 lg:p-8 max-w-7xl mx-auto w-full">
          {children}
        </main>

        {/* Bottom Tab Bar (<640px) */}
        <MobileTabBar />
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
        <DialogContent className="max-w-md p-6 bg-card dark:bg-zinc-950 border-border/80 dark:border-zinc-800">
          <DialogHeader className="text-left space-y-2">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 dark:bg-red-500/20 text-red-500 mb-1">
              <AlertTriangle className="h-6 w-6 stroke-[2]" />
            </div>
            <DialogTitle className="text-base font-semibold text-foreground">
              Sign Out of {APP_NAME}?
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground leading-relaxed">
              Are you sure you want to end your current session?
            </DialogDescription>
          </DialogHeader>

          {currentUser && (
            <div className="flex items-center gap-3 p-3 my-2 rounded-xl bg-muted/40 dark:bg-zinc-900 border border-border/60 dark:border-zinc-800/80">
              <img
                src={currentUser.avatar_url}
                alt={currentUser.name}
                className="h-9 w-9 rounded-full bg-muted object-cover ring-1 ring-border/70 dark:ring-white/10"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'><rect width='32' height='32' fill='%2318181b'/><text x='16' y='21' font-size='13' font-family='sans-serif' font-weight='600' text-anchor='middle' fill='white'>AD</text></svg>";
                }}
              />
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-xs font-semibold truncate text-foreground">
                  {currentUser.name}
                </span>
                <span className="text-[11px] text-muted-foreground truncate">
                  {currentUser.email_id}
                </span>
              </div>
              <span className="text-[10px] uppercase font-mono px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground font-semibold">
                {currentUser.role}
              </span>
            </div>
          )}

          <DialogFooter className="flex-row items-center justify-end gap-2.5 mt-5">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowLogoutConfirm(false)}
              className="text-xs h-9 px-4 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmLogout}
              className="text-xs h-9 px-4 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium gap-1.5 shadow-sm"
            >
              <LogOut className="h-3.5 w-3.5" />
              <span>Yes, Sign Out</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
